import {createSelector} from '@reduxjs/toolkit'
import {getOfferFromBookingData} from 'business/getOfferFromBookingData'
import {getHttpRequestId} from 'modules/common/selectors'
import {find, last, pathOr, pipe, propEq, propOr, whereEq} from 'ramda'
import bofhDefaultHeaders from 'services/bofhDefaultHeaders'
import Settings from 'Settings'
import {RootState} from 'store'
import buildUrl from 'utils/buildUrl'
import {urlParams} from 'utils/urlParams'
import {getBookingManagementUrl} from 'utils/urls'

import {BookingData} from '@daedalus/core/src/booking/types/BookingData'
import {BookingStatus} from '@daedalus/core/src/booking/types/BookingStatus'
import {defaultLanguage} from '@daedalus/core/src/localization/services/locale'
import {ActionLinkTypes} from '@daedalus/core/src/offer/types/actionLink'
import {Offer} from '@daedalus/core/src/offer/types/offer'
import {Room} from '@daedalus/core/src/room/types/RoomConfiguration'
import {getCookie} from '@daedalus/core/src/utils/cookies'
import {getUserDeviceDetails} from '@daedalus/core/src/utils/userAgent'

export const buildBookingManagementLink = (
  searchParams: Record<string, unknown>
) => {
  return buildUrl(getBookingManagementUrl(), searchParams)
}

export const bookingResponse = (state: RootState) =>
  state?.book?.booking?.response

export const getIsSuccessFullyBooked = (state: RootState) => {
  const bookingStatusConfirmed = whereEq({status: BookingStatus.CONFIRMED})
  return bookingStatusConfirmed(state?.book?.booking)
}

export const getBookingModuleStatus = (state: RootState): string =>
  state?.book?.offerBookState || ''

export const getConfirmationId = (state: RootState) =>
  pathOr<string>('', ['book', 'booking', 'response', 'confirmationId'], state)

export const getBookingId = (state: RootState) =>
  pathOr<string | undefined>(undefined, ['book', 'booking', 'id'], state)

export const getBookingStatus = (state: RootState) =>
  pathOr<BookingStatus | undefined>(
    undefined,
    ['book', 'booking', 'status'],
    state
  )

export const getBillingInfo = (state: RootState) =>
  pathOr('', ['book', 'booking', 'request', 'payment', 'billing'], state)

export const getRooms = (state: RootState) =>
  pathOr<Room[]>([], ['book', 'booking', 'response', 'rooms'], state)

export const getRoomByConfirmationId = (
  state: RootState,
  confirmationId: string
) => {
  return pipe(
    getRooms,
    find<Room>(propEq('confirmationId', confirmationId))
  )(state)
}

export const getLastError = (state: RootState) => last(state.book.errors)

export const getOfferBookState = (state: RootState) =>
  state?.book?.offerBookState

export const getOfferInfo = (state: RootState): null | Offer => {
  const bookingResponse = pathOr<null | BookingData>(
    null,
    ['book', 'booking', 'response'],
    state
  )
  return bookingResponse ? getOfferFromBookingData(bookingResponse) : null
}

export const getExistingBookingQueryParams = createSelector(
  getHttpRequestId,
  httpRequestId => {
    const {bookingId = '', locale: urlLocale} = urlParams()
    const locale = urlLocale || getCookie('locale') || defaultLanguage
    const {deviceCategory: deviceType} = getUserDeviceDetails()
    const headers = {...bofhDefaultHeaders, 'X-Request-Id': httpRequestId}

    return {
      url: Settings.get('REACT_APP_BOFH_ENDPOINT'),
      bookingId,
      deviceType,
      locale,
      headers
    }
  }
)

export const getBookingInfo = ({
  state,
  searchParams
}: {
  state: RootState
  searchParams: Record<string, unknown>
}) => {
  const bookingId = getBookingId(state)
  const manageBookingLink = pipe(
    pathOr([], ['book', 'booking', 'response', 'links']),
    find(propEq('type', ActionLinkTypes.MANAGE_BOOKING)),
    propOr(buildBookingManagementLink({...searchParams, bookingId}), 'href')
  )(state)

  const email = pathOr<string>(
    '',
    ['book', 'booking', 'response', 'customer', 'email'],
    state
  )

  const merchantOfRecord = pathOr<string>(
    '',
    ['book', 'booking', 'response', 'merchantOfRecord'],
    state
  )

  const customerSupport = pathOr(
    [],
    ['book', 'booking', 'response', 'customerSupport']
  )(state)

  return {
    confirmationId: getConfirmationId(state),
    manageBookingLink,
    customerSupport,
    email,
    merchantOfRecord
  }
}

export const getCountryCode = (state: RootState) => {
  return pathOr(
    '',
    [
      'book',
      'booking',
      'request',
      'payment',
      'billing',
      'address',
      'countryCode'
    ],
    state
  )
}
